/**
 * Editor-scoped state. The CityEditor host calls `openPlan(id)` on mount and
 * `closeEditor()` on destroy; everything else here is stable across the
 * session (so Svelte components can subscribe at top level without churn).
 *
 * State design:
 *   - `editorPlan` is the live in-memory plan. Commands mutate this; autosave
 *     debounces a write back to Dexie. Reloading the page reads from Dexie,
 *     so the at-most-300ms unsaved window is the only data-loss surface.
 *   - The undo/redo stack and the autosave timer are tied to the *opened*
 *     planId. Switching plans clears both.
 *   - Validation is derived: it re-runs whenever the plan or imported city
 *     change. Cheap enough at 500 buildings to skip explicit memoisation.
 */
import { derived, get, writable, type Readable, type Writable } from 'svelte/store'
import * as plansStore from '$lib/storage/plans'
import * as importsStore from '$lib/storage/imports'
import * as settingsStore from '$lib/storage/settings'
import { resolveBuilding } from '$lib/catalogue'
import {
  apply,
  createAutosave,
  UndoStack,
  validatePlan,
  type Autosave,
  type Command,
  type ValidationResult,
} from '$lib/editor'
import type { ImportedCity, Plan } from '$lib/types'

export interface EditorState {
  loading: boolean
  error: string | null
}

const stateStore: Writable<EditorState> = writable({ loading: false, error: null })
const planStore: Writable<Plan | null> = writable(null)
const importStore: Writable<ImportedCity | null> = writable(null)

export const editorState: Readable<EditorState> = stateStore
export const editorPlan: Readable<Plan | null> = planStore
export const editorImport: Readable<ImportedCity | null> = importStore

export const selectedBuildingId: Writable<number | null> = writable(null)
export const hoveredBuildingId: Writable<number | null> = writable(null)

export const undoStack = new UndoStack()
const undoVersion: Writable<number> = writable(0)
function bumpUndo(): void {
  undoVersion.update((n) => n + 1)
}
export const undoCanUndo: Readable<boolean> = derived(undoVersion, () => undoStack.canUndo())
export const undoCanRedo: Readable<boolean> = derived(undoVersion, () => undoStack.canRedo())

export const validation: Readable<ValidationResult> = derived(
  [planStore, importStore],
  ([$plan, $import]) => {
    if (!$plan) return { ok: true, errors: [], invalidBuildingIds: new Set<number>() }
    return validatePlan($plan, (id) => resolveBuilding(id, $import ?? undefined))
  },
)

let autosave: Autosave | null = null
let openedPlanId: number | null = null

export async function openPlan(planId: number): Promise<void> {
  if (openedPlanId === planId && get(planStore)?.id === planId) return
  await closeEditor()
  stateStore.set({ loading: true, error: null })
  try {
    const plan = await plansStore.get(planId)
    if (!plan) {
      stateStore.set({ loading: false, error: 'Plan not found' })
      return
    }
    const importedCity = (await importsStore.get(plan.importedCityId)) ?? null
    planStore.set(plan)
    importStore.set(importedCity)
    selectedBuildingId.set(null)
    hoveredBuildingId.set(null)
    undoStack.clear()
    bumpUndo()
    autosave = createAutosave(planId)
    openedPlanId = planId
    stateStore.set({ loading: false, error: null })
    await settingsStore.setLastOpenedPlan(planId)
  } catch (err) {
    stateStore.set({ loading: false, error: err instanceof Error ? err.message : String(err) })
  }
}

export async function closeEditor(): Promise<void> {
  // Flush in-flight autosave so the at-most-300ms unsaved window collapses to
  // zero on navigation — the user never loses an edit they could see on screen.
  if (autosave) {
    try {
      await autosave.flush()
    } catch (err) {
      console.error('Autosave flush failed on close:', err)
    }
    autosave = null
  }
  openedPlanId = null
  planStore.set(null)
  importStore.set(null)
  selectedBuildingId.set(null)
  hoveredBuildingId.set(null)
  undoStack.clear()
  bumpUndo()
  stateStore.set({ loading: false, error: null })
}

/**
 * Apply a command, push the inverse to undo, queue an autosave. The renderer
 * subscribes to `editorPlan` and re-derives renderables on every change.
 *
 * Returns the command result so callers can chain (e.g. select the new
 * building after a duplicate).
 *
 * `plan.version` ticks here on every successful command (forward, undo, redo).
 * Pure-logic commands in `lib/editor/commands.ts` stay version-agnostic; the
 * counter is editor-scoped session metadata for memo invalidation downstream
 * (connectivity validation, Phase 4 stats, Phase 5 snapshot diffs).
 */
function bumpVersion(plan: Plan): Plan {
  return { ...plan, version: plan.version + 1 }
}

export function dispatch(command: Command): { newPlan: Plan; inverse: Command } | null {
  const plan = get(planStore)
  if (!plan) return null
  try {
    const result = apply(plan, command)
    const versioned = bumpVersion(result.newPlan)
    planStore.set(versioned)
    undoStack.push(command, result.inverse)
    bumpUndo()
    autosave?.queue(versioned)
    return { newPlan: versioned, inverse: result.inverse }
  } catch (err) {
    console.error('Command apply failed:', err)
    return null
  }
}

export function undo(): void {
  const entry = undoStack.undo()
  if (!entry) return
  const plan = get(planStore)
  if (!plan) return
  try {
    const result = apply(plan, entry.inverse)
    const versioned = bumpVersion(result.newPlan)
    planStore.set(versioned)
    autosave?.queue(versioned)
    bumpUndo()
  } catch (err) {
    console.error('Undo failed:', err)
  }
}

export function redo(): void {
  const entry = undoStack.redo()
  if (!entry) return
  const plan = get(planStore)
  if (!plan) return
  try {
    const result = apply(plan, entry.command)
    const versioned = bumpVersion(result.newPlan)
    planStore.set(versioned)
    autosave?.queue(versioned)
    bumpUndo()
  } catch (err) {
    console.error('Redo failed:', err)
  }
}

/**
 * Replace the in-memory plan wholesale and write it through immediately.
 * Used by Reset-to-import — the operation isn't undoable, so it also drops
 * the undo/redo stack.
 */
export async function replacePlan(updates: Partial<Plan>): Promise<void> {
  const plan = get(planStore)
  if (!plan || openedPlanId === null) return
  await plansStore.update(openedPlanId, updates)
  const fresh = await plansStore.get(openedPlanId)
  if (fresh) planStore.set(fresh)
  selectedBuildingId.set(null)
  undoStack.clear()
  bumpUndo()
}

/** Update the in-memory plan after a side-channel change (e.g. rename). */
export async function reloadPlan(): Promise<void> {
  if (openedPlanId === null) return
  const fresh = await plansStore.get(openedPlanId)
  if (fresh) planStore.set(fresh)
}
